import React, { useCallback, useState, useRef, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useT } from '~app/core/BundleProvider';
import { actions as CanvasActions } from '~app/features/canvas';
import { actions as SaveLoadActions } from '~app/features/saveLoad';
import { actions as GradingActions } from '~app/features/gradingTool';
import ResultsTableRow from './ResultsTableRow';
import ProgressBar from './ProgressBar';
import AnswerKeyPage from './AnswerKeyPage';
import StudentModelsPage from './StudentModelsPage';
import ResultsPage from './ResultsPage';
import Canvas from '~app/features/canvas/components/Canvas';
import readFileAsync from '~app/core/readFileAsync';
import Logging from '~app/core/logging';
import Diagram from '~app/models/Diagram';
import AriaModal from 'react-aria-modal';
import ConfirmationDialog from '../../app/components/ConfirmationDialog';

// grading tool page component
const GradingTool = () => {
  const t = useT();
  const dispatch = useDispatch();
  const [currentPage, setCurrentPage] = useState(1);
  const [showSolutionHashMismatch, setShowSolutionHashMismatch] = useState(
    false,
  );
  const fileUploader = useRef();
  const log = Logging.getLogger('app.features.saveLoad.index');
  const clonedSubmissions = [
    ...useSelector(state => state.grading.submissions),
  ];
  const clonedSubmissionUuids = {
    ...useSelector(state => state.grading.submissionUuids),
  };
  const [showReferenceFileError, setShowReferenceFileError] = useState(false);
  const newSubmissions = [];
  const activeSubmissionUuids = { ...clonedSubmissionUuids };
  const fileNumber = clonedSubmissions.length;
  const storeSubmissions = useCallback(
    submissions => {
      dispatch(GradingActions.storeSubmissions(submissions));
    },
    [dispatch],
  );
  const setSubmissionUuids = useCallback(
    uuids => {
      dispatch(GradingActions.setSubmissionUuids(uuids));
    },
    [dispatch],
  );
  const solutionHash = useSelector(
    state => state.document.present?.solutionHash,
  );
  useEffect(() => {
    dispatch(GradingActions.setGradingMode(true));
  }, [dispatch]);

  const handleSubmissions = async files => {
    for (let i = 0; i < files.length; i++) {
      if (files[i].name.endsWith('.model') && files[i].size < 8388608) {
        const fileContents = await readFileAsync(files[i]);
        // TODO: could provide useful messaging to user when new item
        // fails to get added to clonedSubmissions, if UX for that is designed
        const [model, ...err] = Diagram.fromJsonString(fileContents); //validate fileContents
        if (!model) {
          const error = t(...err);
          log.debug(error);
          continue;
        } else {
          let parsedContents = null;
          try {
            parsedContents = JSON.parse(fileContents);
          } catch (e) {
            log.error(e);
            parsedContents = null;
          }
          if (
            parsedContents &&
            parsedContents?.workingModel &&
            parsedContents?.creator && // entry is a student submission
            parsedContents?.uuid &&
            !(parsedContents.uuid in activeSubmissionUuids) // && // entry not already added
            // parsedContents.solutionHash === solutionHash // submission matches up with loaded answer key
          ) {
            newSubmissions.push(parsedContents);
            activeSubmissionUuids[parsedContents.uuid] = ''; // just using object to store set of keys
          }

          if (parsedContents && parsedContents?.solutionHash !== solutionHash) {
            setShowSolutionHashMismatch(true);
          }
        }
      }
    }
    storeSubmissions([...clonedSubmissions, ...newSubmissions]);
    setSubmissionUuids(activeSubmissionUuids);
  };

  const onCancelOfSolutionHashMismatch = () => {
    setCurrentPage(1);
    setShowSolutionHashMismatch(false);
    clearFileUploads();
  };

  const clearFileUploads = () => {
    storeSubmissions([]); // this clears all the uploaded files
    setSubmissionUuids({}); // change the logic if any other behaviour expected
  };

  const handleDragDropSubmissions = files => {
    handleSubmissions(files);
  };
  const handleFilePickerSubmissions = e => {
    const fileList = [];
    for (let i = 0; i < e.target.files.length; i++) {
      fileList.push(e.target.files[i]);
    }
    handleSubmissions(fileList);
  };

  const handleReferenceUpload = useCallback(
    async file => {
      dispatch(CanvasActions.setDefaultEditMode(''));
      if (file.name.endsWith('.model')) {
        const fileContent = await readFileAsync(file);
        const [model, ...err] = Diagram.fromJsonString(fileContent); //validate fileContent
        if (!model) {
          const error = t(...err);
          log.debug(error);
          setShowReferenceFileError(true);
          return;
        }
        if (model.mode !== 'creation') {
          setShowReferenceFileError(true);
          return;
        }
        dispatch(SaveLoadActions.loadFromLocal({ file }));
        dispatch(GradingActions.storeReferenceFile(fileContent));
        setCurrentPage(currentPage + 1);
      } else {
        setShowReferenceFileError(true);
      }
    },
    [dispatch, currentPage, t, log],
  );

  const handleDragDropReference = files => {
    handleReferenceUpload(files[0]);
  };

  const handleReferenceFilePicker = e => {
    const file = e.target.files[0];
    handleReferenceUpload(file);
  };

  const answerKeyButtons = [
    {
      text: t('open'),
      onClick: () => fileUploader.current.click(),
    },
  ];

  const modelButtons = [
    {
      text: t('open'),
      onClick: () => fileUploader.current.click(),
    },
  ];

  const gradeSubmissions = async () => {
    dispatch(CanvasActions.enterComparisonState());
    await dispatch(GradingActions.gradeSubmissions(clonedSubmissions));
    setCurrentPage(currentPage + 1);
    dispatch(CanvasActions.exitComparisonState());
  };

  const handleGradeButtonClick = useCallback(
    e => {
      setCurrentPage(3);
    },
    [setCurrentPage],
  );

  const GradingPage = () => {
    useEffect(() => {
      gradeSubmissions();
    }, []);

    return (
      <>
        <StudentModelsPage
          modelButtons={modelButtons}
          fileUploader={fileUploader}
          handleDragDrop={handleDragDropSubmissions}
          handleFilePicker={handleFilePickerSubmissions}
          setCurrentPage={null}
          fileNumber={fileNumber}
          handleGradeButtonClick={null}
        />
        <div className="gradingCanvasWrap">
          <Canvas />
        </div>
        <div className="loaderOverlay">
          <div className="loaderImage"></div>
        </div>
      </>
    );
  };

  const tableColumns = [
    {
      name: 'Personal_Identifier',
      title: t('personal-identifier'),
      alt: t('sort-by-personal-identifier'),
      sortable: true,
      className: 'Table__col-grade',
    },
    {
      name: 'Student_Score',
      title: t('student-score'),
      alt: t('sort-by-grade'),
      sortable: true,
      className: 'Table__col-grade',
    },
  ];

  const filterData = Results => {
    return Results;
  };

  return (
    <div
      role="main"
      className={`Page__container ${
        currentPage === 1
          ? 'Page__container-answerKey'
          : currentPage === 4
          ? 'Page__container-gradeResults'
          : ''
      }`}
    >
      <h1 className="Table__title">{t('grading-tool')}</h1>
      <ProgressBar step={currentPage} />
      {currentPage === 1 && (
        <AnswerKeyPage
          answerKeyButtons={answerKeyButtons}
          fileUploader={fileUploader}
          handleFileUpload={handleReferenceFilePicker}
          handleDragDrop={handleDragDropReference}
        />
      )}
      {currentPage === 2 && (
        <StudentModelsPage
          modelButtons={modelButtons}
          fileUploader={fileUploader}
          handleDragDrop={handleDragDropSubmissions}
          handleFilePicker={handleFilePickerSubmissions}
          setCurrentPage={setCurrentPage}
          fileNumber={fileNumber}
          handleGradeButtonClick={handleGradeButtonClick}
        />
      )}
      {currentPage === 3 && <GradingPage />}
      {currentPage === 4 && (
        <ResultsPage
          ResultsTableRow={ResultsTableRow}
          tableColumns={tableColumns}
          filterData={filterData}
          setCurrentPage={setCurrentPage}
        />
      )}

      <AriaModal
        mounted={showReferenceFileError}
        titleText={t('file-upload-error-title')}
      >
        <ConfirmationDialog
          title={t('file-upload-error-title')}
          text={t('reference-fileupload-error-text')}
          okLabel={t('ok-button')}
          onOk={() => setShowReferenceFileError(false)}
          cssClass={'FileUploadError__container'}
        />
      </AriaModal>
      <AriaModal
        mounted={showSolutionHashMismatch}
        titleText={t('solutionhash-mismatch-title')}
      >
        <ConfirmationDialog
          title={t('solutionhash-mismatch-title')}
          htmlText={t('solution-hash-mismatch')}
          okLabel={t('continue-upload')}
          cancelLabel={t('cancel-button')}
          onOk={() => setShowSolutionHashMismatch(false)}
          onCancel={() => onCancelOfSolutionHashMismatch()}
          cssClass={'SolutionMismatch__Container'}
        />
      </AriaModal>
    </div>
  );
};

export default GradingTool;
